import {IconButton} from '@mui/material';
import {FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {useBusinessId} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {ManagersForm} from 'src/components/forms/ManagersForm';
import {EditIcon} from 'src/components/icons/EditIcon';
import {BaseUserInfo} from 'src/types';
import theme from 'src/theme';

/**
 * The pencil on a back-office person's row.
 *
 * It used to carry a form of its own - the same three fields, written twice - and the third of them
 * was their kind, which this endpoint no longer changes. One editor instead: the same `ManagersForm`
 * the single manager page opens, so a name, a language, a title and a phone are editable from either.
 */
export const UpdateManager: FC<{ row: BaseUserInfo }> = ({row}) => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const [open, setOpen] = useState<boolean>(false);

    const dialogTitle = intl.formatMessage({id: 'editManager'});
    const handleCloseModal = () => setOpen(false);

    return (
        <>
            <IconButton
                sx={{
                    width: '28px',
                    height: '28px',
                    p: '2px',
                    color: theme.palette.neutral[400],
                    backgroundColor: 'transparent',
                }}
                onClick={() => setOpen(true)}
            >
                <EditIcon/>
            </IconButton>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <ManagersForm
                    handleClose={handleCloseModal}
                    businessId={businessId}
                    isEditMode
                    userId={row.id}
                    initialValues={{
                        firstName: row.firstName ?? '',
                        lastName: row.lastName ?? '',
                        email: row.emailAddress ?? '',
                        userType: row.userType ?? '',
                        language: row.language ?? '',
                        title: row.title ?? '',
                        phoneNumber: row.phoneNumber ?? '',
                    }}
                />
            </AMPModal>
        </>
    );
};
